// BlogDetailsPage/index.tsx
import React, { useEffect, useState } from 'react';
import { 
  Typography, 
  Card, 
  Tag, 
  Divider, 
  Avatar, 
  Space, 
  Breadcrumb,
  Row,
  Col,
  Button,
  Spin,
  Alert,
  Empty
} from 'antd';
import { 
  CalendarOutlined, 
  FolderOutlined, 
  GlobalOutlined,
  LeftOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import './index.css';
import { getBlogById } from '../../apis/fixture.api';
import { formatDateTime } from '../../utils';

const { Title, Text, Paragraph } = Typography;

const BlogDetailsPage = ({ blogData }: any) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!blogData);
  const [error, setError] = useState<string | null>(null);
  const [blog, setBlog] = useState<any>(blogData || null);

  // Fetch blog data from backend
  const fetchBlogData = async (blogId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getBlogById(blogId);
      
      if (response.data) {
        setBlog(response.data);
      } else {
        setError('Blog not found or invalid response from server');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 
        err.message || 
        'Failed to load blog. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && !blogData) {
      fetchBlogData(id);
    }
  }, [id, blogData]);

  const formatReadingTime = (content: any[], conclusion: string) => {
    const totalWords = content.reduce((acc, item) => {
      return acc + (item.paragraph?.split(' ').length || 0);
    }, 0) + (conclusion?.split(' ').length || 0);
    
    const readingTime = Math.ceil(totalWords / 200); // 200 words per minute
    return `${readingTime} min read`;
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRetry = () => {
    if (id) {
      fetchBlogData(id);
    }
  };

  if (loading) {
    return (
      <div className="blog-details-container">
        <div className="blog-details-content">
          <div className="loading-spinner">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-details-container">
        <header className="blog-details-header">
          <Button
            className="blog-details-back-btn"
            icon={<LeftOutlined />}
            onClick={handleBack}
          >
            Back to Blog
          </Button>
        </header>
        
        <div className="blog-details-content">
          <Alert
            message="Error Loading Blog"
            description={error}
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            action={
              <Button 
                type="primary" 
                onClick={handleRetry}
                className="action-button"
              >
                Retry
              </Button>
            }
            style={{ 
              marginBottom: 24,
              background: 'rgba(255, 87, 87, 0.1)',
              border: '1px solid rgba(255, 87, 87, 0.3)'
            }}
          />
          
          <Empty
            description={
              <Text style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                The blog post could not be loaded.
              </Text>
            }
          />
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-details-container">
        <header className="blog-details-header">
          <Button
            className="blog-details-back-btn"
            icon={<LeftOutlined />}
            onClick={handleBack}
          >
            Back to Blog
          </Button>
        </header>
        
        <div className="blog-details-content">
          <Alert
            message="Blog Not Found"
            description="The blog post you're looking for doesn't exist or has been removed."
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ 
              marginBottom: 24,
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)'
            }}
          />
          
          <Empty
            description={
              <Text style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                No blog post found
              </Text>
            }
          />
        </div>
      </div>
    );
  }

  const readingTime = formatReadingTime(blog.content, blog.conclusion);
  const hasImage = blog.image && blog.image.trim() !== '';

  return (
    <div className="blog-details-container">
      <header className="blog-details-header">
        <Button
          className="blog-details-back-btn"
          icon={<LeftOutlined />}
          onClick={handleBack}
        >
          Back to Blog
        </Button>
        <div className="site-badge">
          <GlobalOutlined />
          {blog.site}
        </div>
      </header>

      <main className="blog-details-content">
        <Breadcrumb
          className="blog-details-breadcrumb"
          items={[
            { title: 'Home', onClick: () => navigate('/') },
            { title: 'Blogs', onClick: () => navigate('/blogs') },
            { title: blog.category },
            { title: blog.title.length > 30 ? `${blog.title.substring(0, 30)}...` : blog.title },
          ]}
        />

        <Card className="blog-details-card">
          <Row gutter={[32, 32]} className="blog-details-grid">
            <Col xs={24} lg={16}>
              {/* Blog Image - if available */}
              {hasImage && (
                <div 
                  className="blog-image-container"
                  style={{ 
                    marginBottom: 32,
                    borderRadius: 8,
                    overflow: 'hidden',
                    maxHeight: 400
                  }}
                >
                  <img 
                    src={blog.image!} 
                    alt={blog.title}
                    style={{ 
                      width: '100%',
                      height: 'auto',
                      objectFit: 'cover',
                      borderRadius: 8
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              <Title level={1} className="blog-details-title">
                {blog.title}
              </Title>

              <div className="blog-details-meta">
                <div className="meta-item">
                  <CalendarOutlined />
                  <Text>{formatDateTime(blog.createdAt)}</Text>
                </div>
                <div className="meta-item">
                  <ClockCircleOutlined />
                  <Text>{readingTime}</Text>
                </div>
                <div className="meta-item">
                  <FolderOutlined />
                  <Tag className="meta-tag">
                    {blog.category}
                  </Tag>
                </div>
                <div className="meta-item">
                  <Tag className="meta-tag-secondary">
                    {blog.type}
                  </Tag>
                </div>
              </div>

              <div style={{ marginTop: 32 }}>
                {blog.content && blog.content.length > 0 ? (
                  blog.content.map((section: any, index: number) => (
                    <div key={index} className="content-section">
                      {section.heading && (
                        <Title level={3} className="content-heading">
                          {section.heading}
                        </Title>
                      )}
                      {section.paragraph && (
                        <Paragraph className="content-paragraph">
                          {section.paragraph}
                        </Paragraph>
                      )}
                    </div>
                  ))
                ) : (
                  <Alert
                    message="No Content Available"
                    description="This blog post doesn't have any content yet."
                    type="info"
                    showIcon
                    style={{ 
                      marginBottom: 32,
                      background: 'rgba(52, 207, 171, 0.1)',
                      border: '1px solid rgba(52, 207, 171, 0.3)'
                    }}
                  />
                )}

                {blog.conclusion && blog.conclusion.trim() !== '' && (
                  <>
                    <Divider className="blog-details-divider" />
                    <Card className="conclusion-card">
                      <Title level={4} className="conclusion-title">
                        Key Takeaways
                      </Title>
                      <Paragraph className="content-paragraph">
                        {blog.conclusion}
                      </Paragraph>
                    </Card>
                  </>
                )}

                <div style={{ marginTop: 32, textAlign: 'center' }}>
                  {blog.site && (
                    <Button 
                      className="action-button"
                      size="large"
                      icon={<GlobalOutlined />}
                      onClick={() => {
                        // Handle different URL formats
                        const siteUrl = blog.site.startsWith('http') 
                          ? blog.site 
                          : 'https://sportsfeed24.st';
                        window.open(siteUrl, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      Visit {blog.site.replace('https://', '').replace('http://', '').replace('www.', '')}
                    </Button>
                  )}
                </div>
              </div>
            </Col>

            <Col xs={24} lg={8} className="sidebar-col">
              <Card className="sidebar-card">
                <Title level={4} className="sidebar-title">
                  Blog Details
                </Title>
                
                <div className="sidebar-details">
                  {blog.category && (
                    <div className="detail-item">
                      <Text className="detail-label">Category</Text>
                      <div>
                        <Tag className="meta-tag">
                          {blog.category}
                        </Tag>
                      </div>
                    </div>
                  )}

                  {blog.type && (
                    <div className="detail-item">
                      <Text className="detail-label">Type</Text>
                      <div>
                        <Tag className="meta-tag-secondary">
                          {blog.type}
                        </Tag>
                      </div>
                    </div>
                  )}

                  {blog.createdAt && (
                    <div className="detail-item">
                      <Text className="detail-label">Published</Text>
                      <div className="detail-value">
                        <CalendarOutlined />
                        {formatDateTime(blog.createdAt)}
                      </div>
                    </div>
                  )}

                  {blog.content && blog.conclusion && (
                    <div className="detail-item">
                      <Text className="detail-label">Reading Time</Text>
                      <div className="detail-value">
                        <ClockCircleOutlined />
                        {readingTime}
                      </div>
                    </div>
                  )}

                  {blog.site && (
                    <div className="detail-item">
                      <Text className="detail-label">Site</Text>
                      <div>
                        <div className="site-badge" style={{ fontSize: '0.9rem' }}>
                          <GlobalOutlined />
                          {blog.site.replace('https://', '').replace('http://', '').replace('www.', '')}
                        </div>
                      </div>
                    </div>
                  )}

                  {blog.id && (
                    <div className="detail-item">
                      <Text className="detail-label">Post ID</Text>
                      <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontFamily: 'monospace' }}>
                        {blog.id}
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="sidebar-card author-section">
                <Title level={4} className="sidebar-title">
                  About the Author
                </Title>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space>
                    <Avatar 
                      size={48}
                      className="author-avatar"
                    >
                      {blog.site ? blog.site.charAt(0).toUpperCase() : 'B'}
                    </Avatar>
                    <div className="author-info">
                      <Text strong className="author-name">
                        {blog.site ? `${blog.site} Team` : 'Blog Author'}
                      </Text>
                      <Text className="author-role">
                        {blog.category ? `${blog.category} Content Specialist` : 'Content Writer'}
                      </Text>
                    </div>
                  </Space>
                  <Paragraph className="author-bio">
                    Expert team dedicated to bringing you the latest in {blog.category || 'sports'} 
                    streaming and coverage. Providing reliable, up-to-date information for 
                    enthusiasts worldwide.
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Button
            className="back-to-blog-btn"
            icon={<LeftOutlined />}
            onClick={() => navigate('/blogs')}
          >
            View All Blog Posts
          </Button>
        </div>
      </main>
    </div>
  );
};

export default BlogDetailsPage;